// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import produce from 'immer';

import {ResourceType} from 'common/report/constants';
import {insertAtIndex, replaceByIndex} from 'common/util/array';
import {DisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {FILTER_VALUE_ATTRIBUTES, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  getFirstSegmentValue,
  getSegmentValueResourceType,
  isCohortSegment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {Segmentation} from 'common/types/reports/query';
import {getAlphaLabelFromIndex} from 'common/util';
import {replaceFormulaReferences} from 'common/report/insights/contexts/formula-utils';
import {TopLevelMetricType} from 'common/types/reports/bookmark';

import {
  Entry,
  isFilterEntry,
  isFormulaEntry,
  isMetricEntry,
  isPropertyEntry,
  isSegmentEntry,
  QueryFilterEntry,
  QuerySegmentEntry,
  RowTypeOption,
  SelectorTypeDefinitions,
} from '../types';
// eslint-disable-next-line import/no-cycle
import {helpers as FilterRowHelpers} from '../row-types/filter/helpers';
import {isEventBasedBehavior, isEventBasedBehavioralSegmentation} from '../row-types/behavior/utils';

function filterToBreakdown(entry: QueryFilterEntry) {
  //Filter to breakdown
  if (entry.event.resourceType === ResourceType.Cohort || isFirstTimeEventFilter(entry.event as PropertyFilter)) {
    // skip cohorts and first time filters for now
    return undefined;
  }
  const event = omit(entry.event, FILTER_VALUE_ATTRIBUTES) as Segmentation;
  if (entry.event.listItemFilters?.length) {
    // for a list of objects breakdown, we set the selected sub-property (listItemGroup) based on the
    // first sub-property selected in the filter (listItemFilters)
    event.listItemGroup = omit(entry.event.listItemFilters[0], FILTER_VALUE_ATTRIBUTES);
    delete event.listItemFilters;
  }

  // remove hidden or inherited fields from board filter/breakdown
  if (`isInherited` in event) {
    delete event.isInherited;
    delete event.isHidden;
  }

  return {
    ...entry,
    event,
    type: SelectorTypeDefinitions.Segment,
    isHidden: false,
    inheritedFromBoard: false,
  };
}

export function segmentToFilter(entry: QuerySegmentEntry): QueryFilterEntry {
  if (isCohortSegment(entry.event)) {
    return undefined;
  }
  const firstEvent = (Array.isArray(entry.event) ? entry.event[0] : entry.event) as Partial<PropertyFilter>;

  // remove hidden or inherited fields from board filter/breakdown
  if (`isInherited` in firstEvent) {
    delete firstEvent.isHidden;
    delete firstEvent.isInherited;
  }

  const event = PropertyFilterFactory.createFromSegment(firstEvent, {displayContext: DisplayContext.Default});

  event.isHidden = false;

  return {
    ...FilterRowHelpers.getNewEntry(),
    event,
  };
}

function propertyToInlineFilter(section: QueryEntrySection, copiedEntry: Entry, index: number) {
  const entry = section.getEntries(true)[index];
  const filteringCapabilities = entry && section.getRowFilteringCapabilities(entry);
  const currentEntry = section.getEntries(true)[index];

  if (entry && filteringCapabilities.canApplyFilter && !isMetricEntry(currentEntry) && isPropertyEntry(copiedEntry)) {
    const filter: PropertyFilter = (isSegmentEntry(copiedEntry) ? segmentToFilter(copiedEntry) : copiedEntry)
      .event as PropertyFilter;

    const newFilter = new PropertyFilter(filter);
    newFilter.isHidden = false;

    const isInlineFilter = isFirstTimeEventFilter(newFilter);
    if (isInlineFilter && !filteringCapabilities.canApplyFirstTimeEventFilter) {
      return undefined;
    }
    return {
      ...currentEntry,
      filters: [...currentEntry.filters, newFilter],
      inheritedFromBoard: false,
    };
  }
  return undefined;
}

function likeToLike(section: QueryEntrySection, entry: Entry, index: number) {
  const rowProps = section.getEntryProps(entry, index);
  // special case for inline filter to global filter
  if (
    isFilterEntry(entry) &&
    !rowProps.getFilteringCapabilities().canApplyFirstTimeEventFilter &&
    isFirstTimeEventFilter(entry.event as PropertyFilter)
  ) {
    return undefined;
  }
  // remove hidden or inherited fields from board filter/breakdown
  if (isFilterEntry(entry) && `isInherited` in entry.event) {
    delete entry.event.isHidden;
    delete entry.event.isInherited;
  }

  return {
    ...entry,
    isHidden: false,
    inheritedFromBoard: false,
  };
}

function isInvalidOtherMove(section: QueryEntrySection, entry: Entry): boolean {
  const otherOptions: Set<string> = new Set([
    ...section.props.otherFilterOptions.map((option) => option.value),
    ...section.props.otherGroupByOptions.map((option) => option.value),
  ]);

  let isInvalidFilter = false;
  if (isFilterEntry(entry)) {
    if (isEventBasedBehavior(entry.event.behavior)) {
      isInvalidFilter = !otherOptions.has(entry.event.behavior.behaviorType);
    } else {
      isInvalidFilter =
        entry.event.resourceType === ResourceType.Other && !otherOptions.has(entry.event.propertyName as string);
    }
  }

  let isInvalidBreakdown = false;
  if (isSegmentEntry(entry)) {
    if (isEventBasedBehavioralSegmentation(entry.event)) {
      isInvalidBreakdown = !otherOptions.has(entry.event.behavior.behaviorType);
    } else {
      isInvalidBreakdown =
        getSegmentValueResourceType(entry.event) === ResourceType.Other &&
        !otherOptions.has(getFirstSegmentValue(entry.event).propertyName as string);
    }
  }

  return isInvalidFilter || isInvalidBreakdown;
}

/** This function generates new entries given a section and a section we are trying to paste in */
export function addEntryFromExternalSection(
  toSection: QueryEntrySection,
  fromSection: QueryEntrySection,
  copiedEntry: Entry,
  index: number, // target index
  nextEntryKey: number,
  /** Whether the original entry is being retained or not. */
  copy = false,
): {
  entries: Entry[];
  entry: Entry;
  index: number;
  modifyExisting: boolean;
  formulaRefUpdates?: Record<string, string>;
} {
  if (copiedEntry === undefined) {
    return undefined;
  }

  const canUseRowType = (rowType: RowTypeOption) =>
    toSection.getAllowedDropRowTypes().some((type) => type === rowType || type[`value`] === rowType);

  let entry;
  let modifyExisting = false;
  const entryRowType = isMetricEntry(copiedEntry) ? copiedEntry.behavior.type : copiedEntry.type;
  if (isInvalidOtherMove(toSection, copiedEntry)) {
    return undefined;
  } else if (canUseRowType(entryRowType)) {
    entry = likeToLike(toSection, copiedEntry, index);
  } else if (isFilterEntry(copiedEntry) && canUseRowType(SelectorTypeDefinitions.Segment)) {
    entry = filterToBreakdown(copiedEntry);
  } else if (isSegmentEntry(copiedEntry) && canUseRowType(SelectorTypeDefinitions.Filter)) {
    entry = segmentToFilter(copiedEntry);
  } else if (propertyToInlineFilter(toSection, copiedEntry, index)) {
    entry = propertyToInlineFilter(toSection, copiedEntry, index);
    modifyExisting = true;
  } else {
    return undefined;
  }

  if (entry === undefined) {
    return undefined;
  }

  const isSameSection = fromSection === toSection;
  const beforeToEntries = toSection.getEntries(true);

  let entries = cloneDeep(
    modifyExisting
      ? replaceByIndex(beforeToEntries, index, entry)
      : insertAtIndex(beforeToEntries, index, {...entry, entryKey: nextEntryKey}),
  );

  // we remove the old entry if we are moving within the same section
  entries = entries.filter((etr) => {
    const shouldRemove = !copy && isSameSection && etr.entryKey === copiedEntry.entryKey;

    return !shouldRemove;
  });

  // update formula references
  const oldEntries = toSection.getEntries();
  const newEntryKey = modifyExisting ? entry.entryKey : nextEntryKey;

  const formulaRefUpdates = calculateFormulaReferenceChanges(oldEntries, entries);

  // if we've reordered an entry in this section, we need to update its label
  if (copiedEntry && fromSection === toSection) {
    const fromIndex = oldEntries.findIndex((target) => target.entryKey === copiedEntry.entryKey);
    const newIndex = entries.findIndex((target) => target.entryKey === newEntryKey);

    const fromLabel = toSection.getEntryLabel(fromIndex);
    const toLabel = toSection.getEntryLabel(newIndex);

    formulaRefUpdates[fromLabel] = toLabel;
  }

  entries = updateFormulaReferences(entries, formulaRefUpdates);

  return {
    entries,
    entry,
    index,
    modifyExisting,
    formulaRefUpdates,
  };
}

export function handleEntriesReArrangement({
  oldEntries,
  entries,
  fromIndex,
  newIndex,
}: {
  oldEntries: Entry[];
  entries: Entry[];
  fromIndex: number;
  newIndex: number;
}) {
  const formulaRefUpdates = calculateFormulaReferenceChanges(oldEntries, entries);

  const fromLabel = getAlphaLabelFromIndex(fromIndex);
  const toLabel = getAlphaLabelFromIndex(newIndex);

  formulaRefUpdates[fromLabel] = toLabel;

  entries = updateFormulaReferences(entries, formulaRefUpdates);

  return {entries, formulaRefUpdates};
}

/**
 * Calculates changes in formula references between two query section states.
 */
export function calculateFormulaReferenceChanges(oldEntries: Entry[], entries: Entry[]) {
  const referenceUpdates = {};

  // track any existing entries that changed index
  entries.forEach((entry, newIndex) => {
    const oldIndex = oldEntries.findIndex((target) => target.entryKey === entry.entryKey);

    if (oldIndex !== -1 && newIndex !== oldIndex) {
      const oldLabel = getAlphaLabelFromIndex(oldIndex);
      const newLabel = getAlphaLabelFromIndex(newIndex);

      referenceUpdates[oldLabel] = newLabel;
    }
  });

  return referenceUpdates;
}

/**
 * Applies formula references to any shallow formula entries and returns the updated entries.
 */
export function updateFormulaReferences(entries: Entry[], referenceUpdates: Record<string, string>): Entry[] {
  return produce(entries, (draft) => {
    draft.forEach((entry: Entry) => {
      if (entry.type === TopLevelMetricType.NewFormulaEntry) {
        entry.definition = replaceFormulaReferences(entry.definition, referenceUpdates);
      } else if (
        isFormulaEntry(entry) &&
        !entry.event.referencedMetrics &&
        typeof entry.event.definition === `string`
      ) {
        entry.event.definition = replaceFormulaReferences(entry.event.definition, referenceUpdates);
      }
    });
  });
}
